/* istanbul ignore file */
import * as Sentry from '@sentry/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import ResizeObserver from 'resize-observer-polyfill';
// import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { ConfirmationProvider } from '@partful/shared/ui/Confirmation/ConfirmationProvider';
import { SnackBarProvider } from '@partful/shared/ui/SnackBar/SnackBarProvider';
import '@partful/shared/ui/theme.scss';
import { createRoot } from 'react-dom/client';
import { I18nextProvider } from 'react-i18next';
import { ShoppingCartPanelProvider } from 'src/providers/ShoppingCart';
import { TenantProvider } from 'src/providers/Tenant';
import { ThemeProvider } from 'src/providers/Theme';
import { UserProvider } from 'src/providers/User';
import { App } from './App';
import { ErrorBoundary } from './ErrorBoundary';
import i18n from './i18n';
import './index.css';

if (process.env.REACT_APP_ENABLE_SENTRY) {
  Sentry.init({
    dsn: process.env.REACT_APP_SENTRY_DSN,
    environment: process.env.REACT_APP_STAGE,
    integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
    // Tracing
    tracesSampleRate: 1.0, //  Capture 100% of the transactions
    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: ['localhost', process.env.REACT_APP_PRODUCT_INVENTORY_SERVICE_BASE_URL],
    // Session Replay
    replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
  });
}

// // ↶ Use this together with npx msw init public/ to use MSW in development ↷
// import { handlers } from 'src/utils/testing/mocks/handlers';
// import { setupWorker } from 'msw/browser';
// const worker = setupWorker(...handlers);
// worker.start({
//   onUnhandledRequest(req, print) {
//    // Suppress warnings for webpack hoy-reload requests
//     const pathname = req.url.pathname.toString();
//     if (pathname.includes('hot-update') || pathname.includes('.hdr')) {
//       return
//     }
//     print.warning()
//   }
// });
// // Very handy!

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false, // Atm we have retry logic handled via axios retry plugin. Once we move all our fetches to react-query we should remove the plugin and unset this option
      refetchOnMount: false, // Set here for two reasons. One, it is impractical for us to every time in most cases.
      // Second, it is impossible to overwrite per-query settings in tests, and this option makes queries never run in tests.
      refetchOnWindowFocus: false,
    },
  },
});

const ro = new ResizeObserver(() => {});
ro.observe(document.body);

const container = document.getElementById('root');
const root = createRoot(container!);
root.render(
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <TenantProvider>
        <UserProvider>
          <ShoppingCartPanelProvider>
            <ThemeProvider>
              <I18nextProvider i18n={i18n}>
                <SnackBarProvider>
                  <ConfirmationProvider>
                    <App />
                  </ConfirmationProvider>
                </SnackBarProvider>
              </I18nextProvider>
            </ThemeProvider>
          </ShoppingCartPanelProvider>
        </UserProvider>
      </TenantProvider>
      {/* <ReactQueryDevtools initialIsOpen /> // <- Useful debugger for react-query fine tuning! */}
    </QueryClientProvider>
  </ErrorBoundary>
);
